import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateMusicProductPhysical,
  IImportArtistsAndTracksFromDigitalMutation,
  IMassReplaceDuplicateArtistsResponse,
  IUpdateMusicProductPhysical,
} from './types/mutation';
import {
  IMusicProductPhysicalResponse,
  IMusicProductPhysicalSearch,
} from './types/query';

export const useGetMusicProductsPhysicalList = (
  params: IMusicProductPhysicalSearch
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<IMusicProductPhysicalResponse[], DefaultError>({
    queryKey: ['music-product-physical-list', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'MusicProductPhysical/Search',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const { value } = await responseHandler<{
        value: IInfinitePageResponse<IMusicProductPhysicalResponse>;
      }>(response, {
        groupName: `useGetMusicProductsPhysicalList - ${pageParam}`,
      });

      return infiniteHandler<IMusicProductPhysicalResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetMusicProductPhysical = (id: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IMusicProductPhysicalResponse, DefaultError>({
    queryKey: ['music-product-physical', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductPhysical/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetMusicProductPhysical - ${id}`,
        notFoundPath: '/music-product-physical',
      });
    },
    enabled: enabled,
  });
};

export const usePostMusicProductPhysical = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostMusicProductPhysical'
  );
  return useMutation<
    IMusicProductPhysicalResponse,
    DefaultError,
    ICreateMusicProductPhysical
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what:
            body.mainTitle.title ?? i18next.t('music-product-physical.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'MusicProductPhysical',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostMusicProductPhysical',
        notFoundPath: '/music-product-physical',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what:
            data.mainTitle.title ?? i18next.t('music-product-physical.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutMusicProductPhysical = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutMusicProductPhysical'
  );
  return useMutation<
    IMusicProductPhysicalResponse,
    DefaultError,
    IUpdateMusicProductPhysical
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what:
            body.mainTitle.title ?? i18next.t('music-product-physical.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'MusicProductPhysical',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutMusicProductPhysical',
        notFoundPath: '/music-product-physical',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what:
            data.mainTitle.title ?? i18next.t('music-product-physical.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteMusicProductPhysical = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteMusicProductPhysical'
  );
  return useMutation<{}, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('music-product-physical.what'),
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `MusicProductPhysical/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteMusicProductPhysical',
        notFoundPath: '/music-product-physical',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('music-product-physical.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostMusicProductPhysicalFromDigital = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostMusicProductPhysicalFromDigital'
  );
  return useMutation<string, DefaultError, string>({
    mutationFn: async musicProductPhysicalId => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('music-product-physical.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'MusicProductPhysical/FromDigital',
        body: { musicProductPhysicalId },
      });

      return responseHandler(response, {
        groupName: 'usePostMusicProductPhysicalFromDigital',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('music-product-physical.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutImportArtistsAndTracksFromDigital = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutImportArtistsAndTracksFromDigital'
  );
  return useMutation<
    IMusicProductPhysicalResponse,
    DefaultError,
    IImportArtistsAndTracksFromDigitalMutation
  >({
    mutationFn: async body => {
      loading(i18next.t('import-artists-and-tracks.loading'));
      const response = await apiClient({
        method: 'PUT',
        url: 'MusicProductPhysical/ImportArtistsAndTracksFromDigital',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutImportArtistsAndTracksFromDigital',
        notFoundPath: '/music-product-physical',
      });
    },
    onSuccess: () => {
      success(i18next.t('import-artists-and-tracks.success'));
    },
    onError: dismiss,
  });
};

export const usePutMassReplaceDuplicateArtists = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss } = useQueryToast(
    'usePutMassReplaceDuplicateArtistsPhysical'
  );
  return useMutation<IMassReplaceDuplicateArtistsResponse, DefaultError, void>({
    mutationFn: async () => {
      loading(
        i18next.t('music-product-physical.mass-replace-duplicates.loading')
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'MusicProductPhysical/MassReplaceDuplicateArtists',
      });

      return responseHandler(response, {
        groupName: 'usePutMassReplaceDuplicateArtistsPhysical',
        notFoundPath: '/music-product-physical',
      });
    },
    onError: dismiss,
  });
};
